import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import {
  getCombatProOwned, TIER_ORDER,
} from './combatProTiers';
import { CombatProShell, BaseOwnershipCard, TierCard, ComparisonTable, cps } from './CombatProShared';

export default function CombatProPricingPage() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const navigate = useNavigate();
  const { user } = useAuth();
  const owned = getCombatProOwned(user?.entitlements?.combat_system_pro);
  const [tier, setTier] = useState('none');
  const [purchasing, setPurchasing] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    API.get('/v1/combat-commerce/account').then((res) => {
      setTier(res.data.combat_cloud?.status === 'active' ? res.data.combat_cloud.tier : 'none');
    }).catch(() => setTier('none'));
  }, []);

  // Base ownership ($49.99) uses the real existing Stripe checkout (same
  // endpoint the dashboard uses). Tiers below remain placeholder/local.
  async function handleChooseCloud(tierKey) {
    setPurchasing(true); setErr('');
    try {
      const res = await consentCheckout.start(() => API.post('/v1/combat-commerce/cloud/checkout', { tier: tierKey }), { purchaseType: 'combat_pro_cloud', intendedSelector: tierKey });
      // null means the shared controller is showing the acknowledgement instead of completing.
      if (res && res.data.checkout_url) window.location.assign(res.data.checkout_url);
      else setTier(tierKey);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not update Combat PRO Cloud.');
    } finally { setPurchasing(false); }
  }
  async function handlePurchaseBase() {
    setPurchasing(true);
    setErr('');
    try {
      const res = await consentCheckout.start(() => API.post('/api/payments/create-combat-pro', {}), { purchaseType: 'combat_system_pro', intendedSelector: null });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setErr('Could not start checkout. Please try again.');
      }
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not start checkout. Please try again.');
    } finally {
      setPurchasing(false);
    }
  }

  return (
    <CombatProShell title="Combat PRO Pricing" subtitle="Lifetime ownership plus optional monthly memberships." navigate={navigate}>
      <GuardedCheckoutConsent checkout={consentCheckout} />
      <BaseOwnershipCard owned={owned} onPurchase={handlePurchaseBase} purchasing={purchasing} />

      <div style={{ marginTop: 14 }}>
      <div style={{ ...cps.card }}>
        <div style={{ ...cps.sectionTitle, display: 'block', width: '100%', margin: '0 0 14px 0' }}>What Combat PRO Includes</div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            '469 Monsters', '319 Spells', 'Monster Studio', 'Encounter Templates',
            'Unlimited Local Saves', 'Combat Logs', 'Legendary Actions', 'Recharge Tracking',
            'Dice Engine', 'Initiative Tracking', 'Conditions', 'Encounter System',
          ].map((f, i) => (
            <li key={i} style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.85)', display: 'flex', gap: 8, lineHeight: 1.4 }}>
              <span style={cps.tick}>&#10003;</span>{f}
            </li>
          ))}
        </ul>
      </div>
      </div>
      {err ? <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 8 }}>{err}</div> : null}

      <div style={cps.sectionTitle}>Membership Tiers</div>
      <div style={cps.tierGrid}>
        {TIER_ORDER.map((t) => (
          <TierCard key={t} tierKey={t} currentTier={tier} onChoose={handleChooseCloud} busy={purchasing} />
        ))}
      </div>

      <div style={{ marginTop: 24 }}>
      <div style={{ ...cps.card }}>
        <div style={{ ...cps.sectionTitle, display: 'block', width: '100%', margin: '0 0 14px 0' }}>Why Membership?</div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            'Cloud Saves', 'Monster Cloud', 'Spell Cloud', 'Encounter Cloud',
            'Prepared Encounter Storage', 'Custom Content Sync', 'Same cloud features on every plan',
          ].map((f, i) => (
            <li key={i} style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.85)', display: 'flex', gap: 8, lineHeight: 1.4 }}>
              <span style={cps.tick}>&#10003;</span>{f}
            </li>
          ))}
        </ul>
      </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ ...cps.sectionTitle, display: 'block', width: '100%', margin: '0 0 12px 0' }}>Full Comparison</div>
        <ComparisonTable currentTier={tier} />
      </div>

      <div style={{ marginTop: 24 }}>
      <div style={{ ...cps.card }}>
        <div style={{ ...cps.sectionTitle, display: 'block', width: '100%', margin: '0 0 14px 0' }}>FAQ</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            ['Do I need membership?', 'No. Combat PRO works fully on its own. Cloud adds synchronization and more prepared encounter capacity.'],
            ['Do I lose ownership?', 'Never. Combat PRO ownership is a one-time purchase you keep permanently, with or without a membership.'],
            ['Can I cancel?', 'Yes. You can cancel a membership at any time and keep full Combat PRO ownership.'],
            ['Will custom content remain?', 'Yes. Your custom monsters, spells, and templates remain yours. Cloud sync of that content is a membership feature.'],
            ['Can I upgrade later?', 'Yes. You can move up a tier whenever you like and the change applies immediately.'],
          ].map(([q, a], i) => (
            <div key={i}>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: '#fff', marginBottom: 4 }}>{q}</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.55 }}>{a}</div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </CombatProShell>
  );
}
